"use client";

import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, Volume2 } from "lucide-react";
import BaseDialog from "@/components/Modals/BaseDialog";
import { Button } from "@/components/shadcn/button";
import { User, UsersService } from "@/generated";
import { useSpotUser } from "@/utils/spotUser";
import { DATA_KEYS } from "@/utils/useRemoteData";

// The feature being announced. A new id shows a new announcement once to
// every user; the last id each user dismissed is kept in their settings.
const FEATURE_ANNOUNCEMENT = {
  id: "sidenav-2026-10",
  title: "Navigation has been updated!",
  description:
    "Navigation is now smoother across the platform, with a redesigned sidebar and new layouts. Check the video below for more details.",
  videoSrc:
    "https://storage.googleapis.com/noxus-docs-videos/sidenav/sidenav.mp4",
};

const LAST_SEEN_KEY = "last_seen_announcement";
const E2E_USER = "tests-e2e@noxus-testing.com";

export const hasUnseenAnnouncement = (spotUser: User) =>
  !!spotUser.id &&
  spotUser.email !== E2E_USER &&
  spotUser.settings?.[LAST_SEEN_KEY] !== FEATURE_ANNOUNCEMENT.id;

export const FeatureAnnouncementModal = () => {
  const { spotUser } = useSpotUser();
  const queryClient = useQueryClient();
  const [isDismissed, setIsDismissed] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(true);

  const markSeen = useMutation({
    mutationFn: UsersService.updateMe,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.USER] });
    },
  });

  const dismiss = () => {
    setIsDismissed(true);
    markSeen.mutate({
      body: {
        settings: {
          ...spotUser.settings,
          [LAST_SEEN_KEY]: FEATURE_ANNOUNCEMENT.id,
        },
      },
    });
  };

  return (
    <BaseDialog
      isOpen={!isDismissed}
      onOpenChange={(open) => !open && dismiss()}
      title={FEATURE_ANNOUNCEMENT.title}
      description={FEATURE_ANNOUNCEMENT.description}
      descriptionClassName="max-w-none"
      size="5xl"
      compact
      footerActions={[
        {
          label: (
            <>
              <Check />
              Got it!
            </>
          ),
          props: { variant: "ghost", onClick: dismiss },
        },
      ]}
    >
      <div className="relative overflow-hidden rounded-md border border-border">
        <video
          ref={videoRef}
          src={FEATURE_ANNOUNCEMENT.videoSrc}
          className="block aspect-video w-full"
          controls
          autoPlay
          muted
          playsInline
          onVolumeChange={(event) => setIsMuted(event.currentTarget.muted)}
        />
        {isMuted && (
          <Button
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-80 hover:opacity-100"
            onClick={() => {
              if (videoRef.current) videoRef.current.muted = false;
            }}
          >
            <Volume2 />
            Unmute
          </Button>
        )}
      </div>
    </BaseDialog>
  );
};
